import React, { useEffect, useMemo } from 'react'
import qs from 'querystring'
import { getParentDepartment, getSearchCriteria } from '@moonpig/web-core-utils'
import {
  LARGE_CARD_SIZE_ID,
  DispatchCutOffQuery,
  ProductInitialVariantSelectionProvider,
  GIANT_CARD_SIZE_ID,
  RecentlyViewedProvider,
  FavouritesProvider,
  E_CARD_SIZE_ID,
  BasketProvider,
  DispatchCutOffGQL,
  DispatchCutOffQueryVariables,
  createViewItemListGAEvent,
} from '@moonpig/web-shared-products'
import {
  PageEnvironment,
  PageOptions,
  PageContext,
} from '@moonpig/web-core-app'
import { logger } from '@moonpig/web-core-monitoring'
import { useRouter } from '@moonpig/web-core-routing'
import Head from 'next/head'
import {
  DepartmentsEnum,
  Region as GraphQLRegion,
  SortOrder,
  SponsoredProductInput,
} from '@moonpig/web-explore-types-graphql'
import { useApolloClient } from '@moonpig/web-core-graphql'
import { Region } from '@moonpig/web-core-types'
import { useFlags } from '@moonpig/web-explore-flags'
import { trackGAEvent, useTrackGAEventOnce } from '@moonpig/web-core-analytics'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import { BRAND_LABEL } from '@moonpig/web-core-brand/config'
import {
  getContent,
  getSearchParameters,
  getSearchContext,
  useScrollRestoration,
} from '../../utils'
import { PageContent, PageMeta, PageType } from '../../types'
import { CmsContent, SearchFilters, UrlFacet } from './types'
import { ProductSearchData } from '../../utils/productSearch'
import {
  GALLERY_PAGE_SIZE,
  GALLERY_PAGE_TYPE,
  GALLERY_RESULTS_TYPE,
  notRudeFilter,
} from '../../constants'
import { getHeadLinksTags } from '../../utils/gallery/galleryPagination'
import {
  ServiceOptions,
  SearchFeatures,
  SearchRequestResponse,
  createServices,
  loadFeatures,
  loadOptions,
} from '../../services'
import {
  LoadFiltersResponse,
  SearchFilter,
} from '../../services/types/services'
import { SearchStoreProvider } from '../../store/SearchStore'
import { removeDuplicateFilters } from '../../utils/FilteringUtils/filterItemUtils'
import { DrawerProvider } from '../../contexts/drawer'
import { GalleryWrapper } from './GalleryWrapper'
import {
  applySelectedFilters,
  buildInitialState,
  getFiltersById,
  getPersistedFilters,
  removePreAppliedFilters,
} from '../../store/helpers'
import { createViewGalleryResultsGAEvent } from '../../analytics/createViewGalleryResultsGAEvent'
import { useNbaFilters } from '../../utils/nbaFilters'
import { getIsFilterApplied } from '../../utils/getIsFilterApplied'
import { useFindLocaleText } from '../../text-localisation'

export type GalleryDetails = {
  region: Region
  url: string
  department: DepartmentsEnum[]
  searchTerm: string
  promotionId?: string
  sponsoredProducts: SponsoredProductInput[]
}

type GalleryPageProps = {
  initialResponse: SearchRequestResponse
  initialFiltersResponse: LoadFiltersResponse
  page: number
  details: GalleryDetails
  content: CmsContent
  pageOptions?: PageOptions
  searchFilters: SearchFilters
  experiments?: { [key: string]: string } | undefined
  initialData?: ProductSearchData
  urlFacets: UrlFacet[]
  dispatchCutOffData?: DispatchCutOffQuery
  shouldDisplaySoldOutMessage?: boolean
  environment?: PageEnvironment
  meta?: PageMeta
  groupCardProject?: string
  options: ServiceOptions
  features: SearchFeatures
  region: Region
  isInternalUser: boolean
  isOffsetPresent: boolean
}

const getVariantKeyFromFacet = ({
  initialFacets,
}: {
  initialFacets: SearchFilters['facets']
}): string | undefined => {
  const initialSelectedFacet = initialFacets.find(({ facetKey }) =>
    ['giant-cards', 'large-cards', 'e-cards'].includes(facetKey as string),
  )

  switch (initialSelectedFacet?.facetKey) {
    case 'giant-cards':
      return GIANT_CARD_SIZE_ID
    case 'large-cards':
      return LARGE_CARD_SIZE_ID
    case 'e-cards':
      return E_CARD_SIZE_ID
  }
}

export const GalleryPage: PageContent<GalleryPageProps> = ({
  page: initialPage,
  initialFiltersResponse,
  initialResponse,
  details,
  content,
  searchFilters,
  experiments,
  dispatchCutOffData,
  shouldDisplaySoldOutMessage,
  environment,
  urlFacets,
  meta,
  groupCardProject,
  options,
  features,
  region,
  isInternalUser,
  isOffsetPresent,
}) => {
  const { department, searchTerm } = details
  const localiseText = useFindLocaleText()
  const router = useRouter()
  const client = useApolloClient()
  const flags = useFlags()

  const galleryMetaTitle = String(meta?.title.split('|')[0].trim())
  const dynamicTitle =
    initialPage > 1
      ? `${galleryMetaTitle} - ${localiseText(
          'find.page',
        )} ${initialPage} | ${BRAND_LABEL}`
      : `${galleryMetaTitle} | ${BRAND_LABEL}`

  const initialFilters = searchFilters.facets.map(f => ({
    key: f.facetKey,
    group: f.group,
  }))

  const services = useMemo(() => {
    const context = {
      region,
      options,
      features,
      client,
      cookies: getBrowserCookies(),
    }

    return createServices(context)
  }, [client, features, options, region])

  useScrollRestoration()

  const filtersQuery = (clientFilters: SearchFilter[]) =>
    services.loadFilters({
      facets: clientFilters,
      departments: department,
      promotionId: searchFilters.promotionId,
      searchTerm,
    })

  const preAppliedFilters = initialFilters

  const persistedFilters = getPersistedFilters()

  const initialState = buildInitialState({
    pageTitle: content.title,
    pageType: GALLERY_PAGE_TYPE as PageType,
    router,
    department,
    initialResponse,
    searchTerm,
    promotionId: details.promotionId,
    initialFiltersResponse,
    initialFilters,
    persistedFilters,
    preAppliedFilters,
    selectedFilters: getFiltersById({
      filterIds: [...urlFacets.map(f => f.facetKey), ...persistedFilters],
      allFilters: initialFiltersResponse.filters,
    }),
    allFilters: applySelectedFilters(
      removePreAppliedFilters(
        initialFiltersResponse.filters,
        preAppliedFilters,
      ),
      [...persistedFilters, ...urlFacets.map(f => f.facetKey)],
    ),
    filtersQuery,
    services,
    sponsoredProducts: details.sponsoredProducts,
    offset: initialPage * GALLERY_PAGE_SIZE - GALLERY_PAGE_SIZE,
  })

  const headLinkTags = getHeadLinksTags({
    environment,
    path: details?.url,
    canonicalSubPath: meta && meta.canonicalSubPath,
    offset: initialPage * GALLERY_PAGE_SIZE - GALLERY_PAGE_SIZE,
    count: initialResponse.totalCount,
  })

  const hasNbaFilters = useNbaFilters(initialFiltersResponse.filters).length > 0
  const hasRudeFilterEnabled = getIsFilterApplied(
    initialFiltersResponse.filters,
    'not-rude',
    false,
  )

  const variantKey = getVariantKeyFromFacet({
    initialFacets: searchFilters.facets,
  })

  const { trackGAEventOnce } = useTrackGAEventOnce()

  useEffect(() => {
    /* istanbul ignore else */
    if (initialState.results) {
      trackGAEvent(
        createViewItemListGAEvent({
          products: initialState.results?.products,
          label: `${GALLERY_PAGE_TYPE} | ${department[0] || 'n/a'} | ${
            (initialFilters.length &&
              initialFilters[initialFilters.length - 1].key) ||
            'n/a'
          } | ${searchTerm || 'n/a'} | ${content.title}`,
          gallery_data: {
            number_of_results: initialState.results.totalCount,
            product_department: department[0],
            product_filter:
              (initialFilters.length &&
                initialFilters[
                  initialFilters.length - 1
                ].key.toLocaleLowerCase()) ||
              'n/a',
            product_term: searchTerm || 'n/a',
            page_title: content.title,
          },
        }),
      )

      trackGAEventOnce(
        createViewGalleryResultsGAEvent({
          department,
          filters: [
            ...initialFilters,
            ...urlFacets.map(f => ({ key: f.facetKey, group: f.group })),
            ...getFiltersById({
              filterIds: persistedFilters,
              allFilters: initialFiltersResponse.filters,
            }).map(f => ({ key: f.id, group: f.parent })),
          ],
          numberOfResults: initialState.results.totalCount,
          pageTitle: content.title,
          sort: SortOrder.POPULARITY,
          showNbas: hasNbaFilters,
          showRudeProducts: hasRudeFilterEnabled,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ProductInitialVariantSelectionProvider variantKey={variantKey}>
      <RecentlyViewedProvider>
        <BasketProvider>
          <FavouritesProvider
            location={content.title}
            enableToastNotification={flags['enable-fav-toast-notification']}
          >
            <DrawerProvider>
              {headLinkTags && (
                <Head>
                  {headLinkTags.canonical && (
                    <link
                      rel="canonical"
                      key="content-head-canonical"
                      data-testid="content-head-canonical"
                      href={headLinkTags.canonical}
                    />
                  )}
                  {headLinkTags.prev && (
                    <link rel="prev" href={headLinkTags.prev} />
                  )}
                  {headLinkTags.next && (
                    <link rel="next" href={headLinkTags.next} />
                  )}
                  <meta
                    property="og:url"
                    content={headLinkTags.canonical || undefined}
                  />
                  <title key="page-dynamic-title">{dynamicTitle}</title>
                  <meta
                    name="title"
                    content={dynamicTitle}
                    key="dynamic-meta-title"
                  />
                  {isOffsetPresent && (
                    <>
                      <meta
                        data-testid="bingbot"
                        name="bingbot"
                        content="noindex"
                      />
                      <meta
                        data-testid="googlebot"
                        name="googlebot"
                        content="noindex,follow"
                      />
                    </>
                  )}
                </Head>
              )}

              <SearchStoreProvider initialState={initialState}>
                <GalleryWrapper
                  experiments={experiments}
                  dispatchCutOffData={dispatchCutOffData}
                  groupCardProject={groupCardProject}
                  department={department}
                  urlFacets={urlFacets}
                  metaTitle={galleryMetaTitle}
                  content={content}
                  shouldDisplaySoldOutMessage={!!shouldDisplaySoldOutMessage}
                  isInternalUser={isInternalUser}
                  searchFilters={searchFilters.facets}
                />
              </SearchStoreProvider>
            </DrawerProvider>
          </FavouritesProvider>
        </BasketProvider>
      </RecentlyViewedProvider>
    </ProductInitialVariantSelectionProvider>
  )
}

GalleryPage.getInitialProps = async ({
  environment,
  flags,
  getCookie,
  getAllExperimentVariants,
  graphQL,
  modules,
  path,
  platform,
  query: typedQuery,
  region,
}) => {
  const cookies = {
    mnpg_exclude_rude_products: getCookie('mnpg_exclude_rude_products'),
  }

  const responseSettings = getCookie('responsiveSettings')
  const responseSettingsData = qs.parse(responseSettings as string)
  const isInternalUser = /^true$/i.test(
    responseSettingsData.EnableInternalUserMode as string,
  )

  const experiments = await getAllExperimentVariants()

  const searchParameters = getSearchParameters(modules)

  const searchCriteria = getSearchCriteria(
    typedQuery,
    searchParameters.departments,
  )

  const galleryDepartments = searchCriteria.department
  const galleryParentDepartment = getParentDepartment(galleryDepartments[0])

  const options = loadOptions({
    query: typedQuery,
    pageType: GALLERY_PAGE_TYPE,
    platform,
    department: galleryParentDepartment,
  })

  const features = loadFeatures({
    experiments,
    region,
    flags,
  })

  const context = {
    region,
    options,
    features,
    client: graphQL,
    cookies,
  }

  const services = createServices(context)

  const query = typedQuery as unknown as PageContext['query']

  const urlFacets = searchCriteria.filters

  if (searchCriteria.promotionId) {
    searchParameters.searchFilters.promotionId = searchCriteria.promotionId
  }

  const dispatchCutOffPromise = graphQL.query<
    DispatchCutOffQuery,
    DispatchCutOffQueryVariables
  >({
    query: DispatchCutOffGQL,
    variables: {
      filter: [galleryDepartments[0], galleryParentDepartment],
      region: region as GraphQLRegion,
      preview: undefined,
    },
  })

  const searchTerm = typedQuery.q || searchParameters?.searchTerm

  const content = getContent(modules)

  const page = (Number(query.offset || 0) || 0) / GALLERY_PAGE_SIZE + 1

  const { parts } = typedQuery

  const excludeRude =
    cookies.mnpg_exclude_rude_products === 'true' &&
    !parts?.includes('naughty') &&
    !parts?.includes('rude') &&
    galleryParentDepartment === ('ALL_CARDS' as DepartmentsEnum)

  const combinedFacets = [
    ...searchParameters.searchFilters.facets,
    ...urlFacets,
    ...(excludeRude ? [notRudeFilter] : []),
  ]

  const searchFilters: SearchFilter[] = removeDuplicateFilters(combinedFacets)
    .filter(x => x.facetKey)
    .map(x => ({ group: x.group, key: x.facetKey, userApplied: x.userApplied }))

  try {
    const searchQuery = services.search({
      searchTerm,
      filters: searchFilters,
      departments: galleryDepartments,
      sponsoredProducts: searchParameters.sponsoredProducts,
      promotionId: searchParameters.searchFilters.promotionId,
      page,
    })
    const filtersQuery = services.loadFilters({
      facets: searchFilters,
      departments: galleryDepartments,
      promotionId: searchParameters.searchFilters.promotionId,
      searchTerm,
    })

    const [
      { data: dispatchCutOffData },
      initialResponse,
      initialFiltersResponse,
    ] = await Promise.all([dispatchCutOffPromise, searchQuery, filtersQuery])

    const { products, totalCount } = initialResponse
    const shouldDisplaySoldOutMessage = products && products.length === 0

    return {
      props: {
        initialResponse,
        initialFiltersResponse,
        page,
        details: {
          region,
          url: path,
          department: galleryDepartments,
          searchTerm,
          promotionId: searchParameters?.searchFilters?.promotionId,
          sponsoredProducts: searchParameters.sponsoredProducts,
        },
        searchFilters: searchParameters.searchFilters,
        urlFacets,
        content,
        experiments,
        dispatchCutOffData,
        shouldDisplaySoldOutMessage,
        environment,
        groupCardProject: query.groupCardProject as string,
        options,
        features,
        region: region as Region,
        isInternalUser,
        isOffsetPresent: (Number(query.offset || 0) || 0) > 0 || false,
      },
      pageOptions: {
        search: getSearchContext(modules),
        layout: {
          nonStickyHeader: true,
        },
        tracking: {
          type: GALLERY_RESULTS_TYPE,
          sortby: 'popularity - default',
          listData: {
            results: totalCount,
            type: GALLERY_RESULTS_TYPE,
            name: content && content.title,
            terms: searchTerm,
            filters: searchParameters && searchParameters.searchFilters,
          },
        },
      },
    }
  } catch (error) {
    logger.fixToday('Gallery products query failure', undefined, error)
    return {
      pageError: {
        statusCode: 500,
      },
    }
  }
}
